import { ExistingFirmResponse } from '@api/firm/firm.types';
import { MortgageClub } from '@api/mortgage-club/mortgageClub.types';
import { Network } from '@api/network/network.types';
import { AuthorisationType } from '@pages/SignupPage/dtos/firm.dto';
import { SubmissionFunnelTypeEnum } from './types';

export type SubmissionRouteUIConfig = {
  visibleOptions: SubmissionFunnelTypeEnum[];
  selectableOptions: SubmissionFunnelTypeEnum[];
  defaultOption: SubmissionFunnelTypeEnum;
};

function firmHasMortgageClubOption(firm: ExistingFirmResponse): boolean {
  if (firm.authorisationType === AuthorisationType.DIRECTLY_AUTHORISED) {
    return Boolean(firm.mortgageClub);
  }

  return Boolean(firm.network?.mortgageClub);
}

export function getSubmissionRouteUIConfig(firm: ExistingFirmResponse): SubmissionRouteUIConfig {
  if (firm.authorisationType === AuthorisationType.DIRECTLY_AUTHORISED) {
    if (!firmHasMortgageClubOption(firm)) {
      return {
        visibleOptions: [SubmissionFunnelTypeEnum.DIRECT],
        selectableOptions: [],
        defaultOption: SubmissionFunnelTypeEnum.DIRECT,
      };
    }

    return {
      visibleOptions: [SubmissionFunnelTypeEnum.DIRECT, SubmissionFunnelTypeEnum.MORTGAGE_CLUB],
      selectableOptions: [SubmissionFunnelTypeEnum.DIRECT, SubmissionFunnelTypeEnum.MORTGAGE_CLUB],
      defaultOption: SubmissionFunnelTypeEnum.DIRECT,
    };
  }

  if (firm.authorisationType === AuthorisationType.AUTHORISED_REPRESENTATIVE) {
    if (!firmHasMortgageClubOption(firm)) {
      return {
        visibleOptions: [SubmissionFunnelTypeEnum.NETWORK],
        selectableOptions: [],
        defaultOption: SubmissionFunnelTypeEnum.NETWORK,
      };
    }

    return {
      visibleOptions: [SubmissionFunnelTypeEnum.NETWORK, SubmissionFunnelTypeEnum.MORTGAGE_CLUB],
      selectableOptions: [SubmissionFunnelTypeEnum.NETWORK, SubmissionFunnelTypeEnum.MORTGAGE_CLUB],
      defaultOption: SubmissionFunnelTypeEnum.NETWORK,
    };
  }

  return {
    visibleOptions: [SubmissionFunnelTypeEnum.DIRECT],
    selectableOptions: [],
    defaultOption: SubmissionFunnelTypeEnum.DIRECT,
  };
}

export function isValidFunnelTypeForFirm(firm: ExistingFirmResponse, funnelType: SubmissionFunnelTypeEnum): boolean {
  const config = getSubmissionRouteUIConfig(firm);

  if (config.selectableOptions.length === 0) {
    return funnelType === config.defaultOption;
  }

  return config.selectableOptions.includes(funnelType);
}

export function getOrganisationForFunnelType(
  firm: ExistingFirmResponse,
  funnelType: SubmissionFunnelTypeEnum,
): Network | MortgageClub | null {
  if (funnelType === SubmissionFunnelTypeEnum.NETWORK) {
    return firm.network ?? null;
  }

  if (funnelType === SubmissionFunnelTypeEnum.MORTGAGE_CLUB) {
    if (firm.authorisationType === AuthorisationType.DIRECTLY_AUTHORISED) {
      return firm.mortgageClub ?? null;
    }

    return firm.network?.mortgageClub ?? firm.mortgageClub ?? null;
  }

  return null;
}
